import { clsx } from 'clsx';
import { useState } from 'react';

import type { ChangeEvent } from 'react';
import type { IconProps } from 'components';

import styles from './RadioTabSelector.module.css';

import { ControlsButton, FeedbackBox, Label } from 'components';

type ButtonProps = {
  label: string;
  value: string;
  iconName?: IconProps['name'];
  disabled?: boolean;
};

export interface RadioTabSelectorProps {
  /** array with button objects with label, value and optional iconName or disabled-state */
  buttons: ButtonProps[];
  /** name of the radiogroup */
  radioGroupName: string;
  /** innitial value */
  defaultValue?: string;
  /** label takst */
  label?: string;
  /** 'top' | 'left' */
  labelPosition?: 'top' | 'left';
  /** show red star */
  required?: boolean;
  /** error text to display if set */
  notValidatedText?: string;
  /** The callback function that is invoked when the radio tab selector changes value. */
  onSetValue: (value: string | null) => void;
}

export const RadioTabSelector = ({
  buttons,
  radioGroupName,
  defaultValue,
  label,
  labelPosition = 'top',
  required = false,
  notValidatedText = '',
  onSetValue,
}: RadioTabSelectorProps) => {
  const [currentValue, setCurrentValue] = useState<string | null>(defaultValue || null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.target.value);
    onSetValue(event.target.value);
  };

  return (
    <div
      className={clsx(
        styles['radio-tab-selector-button-bar__container'],
        styles[`radio-tab-selector-button-bar__container--label-${labelPosition}`]
      )}
    >
      {label && (
        <Label htmlFor="" required={required}>
          {label}
        </Label>
      )}
      <div className={styles['radio-tab-selector-button-bar__row-container']}>
        <div
          className={clsx(styles['radio-tab-selector-button-bar__row'], {
            [styles['radio-tab-selector-button-bar__row--not-validated']]: notValidatedText,
          })}
        >
          {buttons.map(({ label, value, iconName, disabled }) => (
            <ControlsButton
              type="radio"
              name={radioGroupName}
              key={`${value}`}
              label={label}
              value={value}
              iconName={iconName}
              checked={currentValue === value}
              disabled={disabled}
              onSetValue={handleChange}
            />
          ))}
        </div>
        {notValidatedText !== '' && <FeedbackBox level="error" feedback={notValidatedText} />}
      </div>
    </div>
  );
};
